import { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import SectionLabel from '../common/SectionLabel';
import { usePipeline } from '../../context/PipelineContext';
import { formatCurrency } from '../../utils/formatting';

// Progressively darker Deep Teal shades for each stage (left to right)
const STAGE_COLORS = [
  '#9DD4D9', // Discovery Scheduled - lightest
  '#5FBAC2', // Discovery Complete
  '#2E9DA6', // Strategy Scheduled
  '#006D78', // Strategy Complete
  '#004D54', // Proposal - darkest
];

/**
 * Weighted Pipeline Chart - horizontal stacked bar showing pipeline value by stage.
 * Each segment is sized by weighted value (probability * annualized value).
 * Below the chart: per-stage summary and total unweighted vs weighted comparison.
 */
export default function WeightedPipelineChart() {
  const { stageSummary, pipelineKPIs } = usePipeline();

  // Build a single-row dataset for the horizontal stacked bar
  // Each stage becomes a dataKey with its weighted value
  const chartData = useMemo(() => {
    const row = { name: 'Pipeline' };
    stageSummary.forEach((stage) => {
      row[stage.stageId] = stage.weightedValue;
    });
    return [row];
  }, [stageSummary]);

  // Total unweighted and weighted values
  const totalUnweighted = pipelineKPIs.unweightedValue;
  const totalWeighted = pipelineKPIs.weightedValue;

  // Check if there is any pipeline value at all
  const hasValue = totalWeighted > 0;

  if (!hasValue) {
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <SectionLabel>Weighted Pipeline by Stage</SectionLabel>
        <p className="text-[13px] text-[#888] text-center py-8">
          No active prospects with estimated values in the pipeline.
        </p>
      </div>
    );
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] border border-gray-200 p-3 text-[13px]">
        {payload.map((entry, i) => {
          if (entry.value === 0) return null;
          const stage = stageSummary.find(s => s.stageId === entry.dataKey);
          return (
            <p key={i} style={{ color: STAGE_COLORS[i] || '#006D78' }}>
              {stage?.label || entry.dataKey}: {formatCurrency(entry.value)}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Weighted Pipeline by Stage</SectionLabel>
      <p className="text-[11px] font-light text-[#888] mb-4">
        Each segment sized by probability-weighted annualized value
      </p>

      <ResponsiveContainer width="100%" height={80}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
        >
          <CartesianGrid horizontal={false} stroke="#e5e7eb" />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: '#888' }}
            tickLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
          />
          <YAxis type="category" dataKey="name" hide />
          <Tooltip content={<CustomTooltip />} />
          {stageSummary.map((stage, idx) => (
            <Bar
              key={stage.stageId}
              dataKey={stage.stageId}
              stackId="pipeline"
              fill={STAGE_COLORS[idx] || '#006D78'}
              radius={
                idx === 0
                  ? [4, 0, 0, 4]
                  : idx === stageSummary.length - 1
                    ? [0, 4, 4, 0]
                    : [0, 0, 0, 0]
              }
            />
          ))}
        </BarChart>
      </ResponsiveContainer>

      {/* Per-stage text summary */}
      <div className="mt-4 grid grid-cols-5 gap-3">
        {stageSummary.map((stage, idx) => (
          <div key={stage.stageId} className="text-center">
            <div
              className="w-3 h-3 rounded-full mx-auto mb-1"
              style={{ backgroundColor: STAGE_COLORS[idx] || '#006D78' }}
            />
            <p className="text-[11px] font-bold text-navy">{stage.label}</p>
            <p className="text-[11px] text-[#888]">
              {stage.count} prospect{stage.count !== 1 ? 's' : ''}
            </p>
            <p className="text-[12px] font-bold text-deep-teal">
              {formatCurrency(stage.weightedValue)}
            </p>
          </div>
        ))}
      </div>

      {/* Weighted total */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex justify-center gap-8 text-[13px]">
        <div className="text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-deep-teal">Weighted Pipeline</p>
          <p className="text-[18px] font-bold text-deep-teal">{formatCurrency(totalWeighted)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#999]">Unweighted</p>
          <p className="text-[18px] font-bold text-[#999]">{formatCurrency(totalUnweighted)}</p>
        </div>
      </div>
    </div>
  );
}
